import {
  compareDmyDates,
  compareNumber,
  compareText,
  includesNormalized,
  uniqueTruthyValues,
} from '../../../../../utils/tableUtils';
import { DEFAULT_FILTERS, SORT_FIELDS } from './table-hac.constants';

const SORT_COMPARATORS = {
  [SORT_FIELDS.NAME]: (a, b) => compareText(a.name, b.name),
  [SORT_FIELDS.LAST_NAME]: (a, b) => compareText(a.last_name, b.last_name),
  [SORT_FIELDS.RETAILER]: (a, b) => compareText(a.retailer_name, b.retailer_name),
  [SORT_FIELDS.PMA_TYPE]: (a, b) => compareNumber(a.pma_type, b.pma_type),
  [SORT_FIELDS.LAST_UPDATE]: (a, b) => compareDmyDates(a.last_update, b.last_update),
};

const normalizePhone = (value) => String(value ?? '').replace(/\D/g, '');

const getRowKey = (row) => [
  row.id,
  row.pma_type ?? 'none',
  row.retailer_name ?? 'no-retailer',
  row.area_code ?? 'no-country',
].join('-');

const sortHacsForPma = (rows = []) => [...rows].sort((a, b) => {
  const dateA = new Date(a.last_update.split('-').reverse().join('-'));
  const dateB = new Date(b.last_update.split('-').reverse().join('-'));

  if (a.status === 'exited' && b.status !== 'exited') return 1;
  if (a.status !== 'exited' && b.status === 'exited') return -1;

  if (a.pma_type === 1 && b.pma_type !== 1) return -1;
  if (a.pma_type !== 1 && b.pma_type === 1) return 1;
  if (a.pma_type === 2 && b.pma_type !== 2) return -1;
  if (a.pma_type !== 2 && b.pma_type === 2) return 1;
  if (a.pma_type === null && b.pma_type !== null) return -1;
  if (a.pma_type === null && b.pma_type === null) return 0;

  return dateB - dateA;
});

const filterHacs = (rows = [], filters = DEFAULT_FILTERS) => {
  let filtered = [...rows];

  if (filters.name) {
    const q = filters.name.toLowerCase();
    filtered = filtered.filter(
      (hac) =>
        includesNormalized(hac.name, q) ||
        includesNormalized(hac.last_name, q)
    );
  }

  if (filters.phone) {
    const search = normalizePhone(filters.phone);
    if (search) {
      filtered = filtered.filter((hac) =>
        normalizePhone(`${hac.prefix_phone ?? ''}${hac.phone ?? ''}`).includes(search)
      );
    }
  }

  if (filters.pma_type) {
    filtered = filtered.filter((hac) => String(hac.pma_type) === filters.pma_type);
  }

  if (filters.country) {
    filtered = filtered.filter((hac) => hac.area_code === filters.country);
  }

  if (filters.retailer) {
    filtered = filtered.filter((hac) => hac.retailer_name === filters.retailer);
  }

  if (filters.pma) {
    filtered = filtered.filter((hac) => hac.pma_id === filters.pma);
  }

  return filtered;
};

const getSurveyStatusConfig = (status) => {
  if (status === 'in_progress') {
    return {
      labelKey: 'common-in-progress',
      className: 'in-progress',
    };
  }

  if (status === 'submitted') {
    return {
      labelKey: 'common-completed',
      className: 'completed',
    };
  }

  return {
    labelKey: 'common-not-started',
    className: 'not-started',
  };
};

const getRowSurveyStatus = (row) => {
  const firstSurvey = Array.isArray(row.surveys) && row.surveys.length > 0 ? row.surveys[0] : null;
  return getSurveyStatusConfig(firstSurvey?.status ?? null);
};

const getRetailerOptions = (rows = [], filters = DEFAULT_FILTERS) => {
  const retailersForCurrentScope = rows
    .filter((hac) => {
      const matchesCountry = filters.country ? hac.area_code === filters.country : true;
      const matchesType = filters.pma_type ? String(hac.pma_type) === filters.pma_type : true;
      return matchesCountry && matchesType;
    })
    .map((hac) => hac.retailer_name);

  return uniqueTruthyValues(retailersForCurrentScope).sort((a, b) => a.localeCompare(b));
};

const getCountryOptions = (countryCodes = [], t) => countryCodes.map((country) => ({
  value: country,
  label: t(`countries-${country}`),
}));

const getRetailerSelectOptions = (retailers = []) => retailers.map((retailer) => ({
  value: retailer,
  label: retailer,
}));

const getPmaSelectOptions = (pmas = []) => [
  ...new Map(
    pmas
      .filter((pma) => pma?.id)
      .map((pma) => [pma.id, {
        value: pma.id,
        label: `${pma.name ?? ''} ${pma.last_name ?? ''}`.trim(),
      }])
  ).values(),
];

export {
  SORT_COMPARATORS,
  filterHacs,
  getCountryOptions,
  getPmaSelectOptions,
  getRetailerOptions,
  getRetailerSelectOptions,
  getRowKey,
  getRowSurveyStatus,
  sortHacsForPma,
};
